import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { useNavigate } from "react-router";
import SearchField from "../../interface/shared/searchfield";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import {
    ProviderNonConformanceReports,
    useNonConformanceReports,
} from "../../api/nonconformance";
import { FadeIn } from "../../interface/shared/animations";

export default function NonConformanceReports() {
    return (
        <DashboardLayout>
            <ProviderNonConformanceReports>
                <NonConformanceReportsContent />
            </ProviderNonConformanceReports>
        </DashboardLayout>
    );
}

function NonConformanceReportsContent() {
    const {
        reports,
        errors,
        loading,
        fetchReports,
        page,
        perPage,
        total,
        setPage,
        setPerPage,
        searchTerm,
        setSearchTerm,
        sortField,
        sortOrder,
        setSort,
    } = useNonConformanceReports();
    const navigate = useNavigate();

    function formatDate(d: Date) {
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleString();
    }

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Non-conformance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Non-conformance reports for the warehouse.
                    </Typography>
                </Box>
                <SearchField
                    placeholder="Search by reference, supplier, or submitter"
                    value={searchTerm}
                    onChange={setSearchTerm}
                />
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => fetchReports()}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Supplier</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Submitted by</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Skeleton variant="rounded" width={70} height={30} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sortDirection={sortField === "created_at" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "created_at"}
                                                direction={sortField === "created_at" ? sortOrder : "asc"}
                                                onClick={() => setSort("created_at")}
                                            >
                                                Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "supplier" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "supplier"}
                                                direction={sortField === "supplier" ? sortOrder : "asc"}
                                                onClick={() => setSort("supplier")}
                                            >
                                                Supplier
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "reference" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "reference"}
                                                direction={sortField === "reference" ? sortOrder : "asc"}
                                                onClick={() => setSort("reference")}
                                            >
                                                Reference
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "submitted_by" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "submitted_by"}
                                                direction={sortField === "submitted_by" ? sortOrder : "asc"}
                                                onClick={() => setSort("submitted_by")}
                                            >
                                                Submitted by
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ py: 4, textAlign: "center" }}
                                                >
                                                    No reports found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        reports.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{formatDate(row.date)}</TableCell>
                                                <TableCell>{row.supplier}</TableCell>
                                                <TableCell>{row.reference}</TableCell>
                                                <TableCell>{row.user.name}</TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                        onClick={() => navigate(`/non-conformance/${row.id}`)}
                                                    >
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
